import {
  firebasePrefix,
  getOnce,
  isSelfReconnecting,
  monitorConnection,
  onChildChanged,
  reconnect,
} from './firebase'
import { isDisplayMessage, normalizeDisplayMessage, type DisplayMessage } from '../types/display'

export interface MessageSource {
  subscribe(onMessage: (message: DisplayMessage) => void): () => void
}

export function cfdPath(placeId: string, userId: string): string {
  return `${firebasePrefix}/cfd/${placeId}/${userId}`
}

/** The cashier wraps every push as `{ data: … }`. */
function unwrap(value: unknown): DisplayMessage | null {
  if (isDisplayMessage(value)) return normalizeDisplayMessage(value)
  if (typeof value === 'object' && value !== null && 'data' in value) {
    const inner = (value as { data: unknown }).data
    if (isDisplayMessage(inner)) return normalizeDisplayMessage(inner)
  }
  return null
}

export function createRtdbSource(placeId: string, userId: string): MessageSource {
  return {
    subscribe(onMessage) {
      const path = cfdPath(placeId, userId)
      let active = true

      let reconnectTimer: ReturnType<typeof setTimeout> | null = null

      const detachChild = onChildChanged(path, (value) => {
        const message = unwrap(value)
        if (active && message) onMessage(message)
      })

      const detachConnection = monitorConnection(
        () => {
          // The disconnect our own reconnect() causes must not schedule another
          // one, or the socket bounces every few seconds forever.
          if (isSelfReconnecting()) return

          console.warn('[Firebase] Disconnected — reconnecting in 3s')
          if (reconnectTimer) clearTimeout(reconnectTimer)
          reconnectTimer = setTimeout(() => reconnect(), 3000)
        },
        () => console.info('[Firebase] Reconnected'),
      )

      void getOnce<unknown>(path).then((value) => {
        const message = unwrap(value)
        if (active && message) onMessage(message)
      })

      return () => {
        active = false
        if (reconnectTimer) clearTimeout(reconnectTimer)
        detachChild()
        detachConnection()
      }
    },
  }
}
